import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import {
  GEOLOCATION_STORAGE_KEY,
  GeolocationContext,
  type GeolocationContextValue,
  type GeolocationState,
} from './geolocation-context';

const INITIAL_STATE: GeolocationState = {
  lat: undefined,
  lng: undefined,
  loading: false,
  granted: false,
  error: null,
};

type PersistedGeolocationState = Pick<GeolocationState, 'granted' | 'lat' | 'lng'>;

function loadPersistedState(): GeolocationState {
  if (typeof window === 'undefined') {
    return INITIAL_STATE;
  }

  try {
    const persistedState = window.localStorage.getItem(GEOLOCATION_STORAGE_KEY);

    if (!persistedState) {
      return INITIAL_STATE;
    }

    const parsedState = JSON.parse(persistedState) as Partial<PersistedGeolocationState>;

    return {
      ...INITIAL_STATE,
      granted: Boolean(parsedState.granted),
      lat: typeof parsedState.lat === 'number' ? parsedState.lat : undefined,
      lng: typeof parsedState.lng === 'number' ? parsedState.lng : undefined,
    };
  } catch {
    return INITIAL_STATE;
  }
}

function persistState(state: PersistedGeolocationState) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(GEOLOCATION_STORAGE_KEY, JSON.stringify(state));
}

function clearPersistedState() {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(GEOLOCATION_STORAGE_KEY);
}

export function GeolocationProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<GeolocationState>(loadPersistedState);
  const shouldRefreshPersistedLocationRef = useRef(
    state.granted && typeof state.lat === 'number' && typeof state.lng === 'number',
  );

  const updateLocation = useCallback((showLoading: boolean) => {
    if (!navigator.geolocation) {
      clearPersistedState();
      setState({
        ...INITIAL_STATE,
        error: 'Geolocalização não suportada',
      });
      return;
    }

    if (showLoading) {
      setState((currentState) => ({
        ...currentState,
        loading: true,
        error: null,
      }));
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        persistState({
          granted: true,
          lat: coords.latitude,
          lng: coords.longitude,
        });
        setState({
          lat: coords.latitude,
          lng: coords.longitude,
          loading: false,
          granted: true,
          error: null,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          clearPersistedState();
        }

        setState((currentState) => ({
          ...(error.code === error.PERMISSION_DENIED ? INITIAL_STATE : currentState),
          loading: false,
          error: error.message,
        }));
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  }, []);

  const requestLocation = useCallback(() => {
    updateLocation(true);
  }, [updateLocation]);

  useEffect(() => {
    if (!shouldRefreshPersistedLocationRef.current) {
      return;
    }

    shouldRefreshPersistedLocationRef.current = false;
    updateLocation(false);
  }, [updateLocation]);

  const value = useMemo<GeolocationContextValue>(
    () => ({
      ...state,
      requestLocation,
    }),
    [requestLocation, state],
  );

  return <GeolocationContext.Provider value={value}>{children}</GeolocationContext.Provider>;
}
